import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { cn } from '@/utils/cn';
import { pageContainer, pageItem } from '@/utils/animations';
import { createGuestUser, storeTokens, getValidAccessToken, clearStoredTokens } from '@/services/authService';
import { clearOnboardingProgress, getOnboardingResumePath } from '@/utils/onboardingProgress';
import { analyticsService } from '@/services/analyticsService';

interface WelcomePageProps {
  className?: string;
}

export const introSlides = [
  { image: '/intro_1.webp', title: 'با شخصیت های داستان حرف بزن' },
  { image: '/intro_2.webp', title: 'هر سوالی داری بپرس' },
  { image: '/intro_3.webp', title: 'روابط و تحلیل شخصیت های داستان رو ببین' },
  { image: '/intro_4.webp', title: 'یادگیری عمیق تر با سوال های کاربردی' },
  { image: '/intro_5.webp', title: 'قبلا چی شده ؟ بعدا چی میشه !' },
  { image: '/intro_6.webp', title: 'دیگه مطالب مهم کتاب رو فراموش نمی‌کنی' },
  { image: '/intro_7.webp', title: 'اتفاقات مهم داستان رو ببین' },
];

const WelcomePage = ({ className }: WelcomePageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);
  const guestCreationInFlight = useRef(false);
  const GUEST_SESSION_KEY = 'booka_guest_session';

  const toast = (location.state as any)?.toast as { type?: 'info' | 'success' | 'warning' | 'error'; message?: string } | undefined;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % introSlides.length);
    }, 1800);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    introSlides.slice(1).forEach(({ image: imageSrc }) => {
      const image = new Image();
      image.decoding = 'async';
      image.src = imageSrc;
    });
  }, []);

  const handleGetStarted = async () => {
    if (guestCreationInFlight.current) return;
    void analyticsService.logEvent('welcome_register_clicked');
    guestCreationInFlight.current = true;
    setIsLoading(true);
    setError('');

    try {
      const existingToken = await getValidAccessToken();
      if (existingToken) {
        navigate(getOnboardingResumePath(), { replace: true });
        return;
      }

      if (sessionStorage.getItem(GUEST_SESSION_KEY) === 'active') {
        sessionStorage.removeItem(GUEST_SESSION_KEY);
        clearOnboardingProgress();
      }

      clearOnboardingProgress();
      const guest = await createGuestUser();
      if (!guest.access_token || !guest.refresh_token) {
        throw new Error('Failed to receive tokens from server');
      }

      await storeTokens(guest.access_token, guest.refresh_token, guest.expires_in || 300, {
        persistNative: false,
      });
      sessionStorage.setItem(GUEST_SESSION_KEY, 'active');
      updateUser({ id: 'guest', authMethod: 'phone', is_guest: true, is_registered: false });
      navigate(getOnboardingResumePath(), { replace: true });
    } catch (e) {
      console.error('[Welcome] Error creating guest user:', e);
      setError(e instanceof Error ? e.message : 'خطا در شروع');
    } finally {
      guestCreationInFlight.current = false;
      setIsLoading(false);
    }
  };

  const handleAlreadyHaveAccount = () => {
    void analyticsService.logEvent('welcome_login_clicked');
    void clearStoredTokens().finally(() => {
      sessionStorage.removeItem(GUEST_SESSION_KEY);
      clearOnboardingProgress();
      navigate('/login', { replace: true });
    });
  };

  return (
    <motion.div
      className={cn('min-h-screen flex flex-col bg-white px-7 py-12 relative overflow-hidden', className)}
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      dir="rtl"
    >
      {toast?.message && (
        <div
          className={cn(
            'w-full max-w-lg mx-auto mb-4 rounded-lg border px-4 py-3 text-sm font-persian relative z-10',
            toast.type === 'error' && 'border-red-300 bg-red-50 text-red-800',
            toast.type === 'warning' && 'border-yellow-300 bg-yellow-50 text-yellow-800',
            (!toast.type || toast.type === 'info') && 'border-blue-300 bg-blue-50 text-blue-800',
            toast.type === 'success' && 'border-green-300 bg-green-50 text-green-800'
          )}
          role="alert"
        >
          {toast.message}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col items-center w-full max-w-auth-mobile md:max-w-auth-tablet lg:max-w-auth-desktop xl:max-w-auth-desktop-lg mx-auto relative z-10">
        <motion.div className="relative -top-8 flex min-h-0 flex-1 w-full flex-col justify-center" variants={pageItem}>
          <div className="relative mx-auto flex min-h-[290px] w-full touch-pan-y items-center justify-center overflow-visible" dir="ltr">
            <div className="mx-auto grid w-full grid-cols-1 justify-items-center">
              {introSlides.map((slide, index) => (
                <div
                  key={slide.image}
                  className={cn(
                    'intro-slide col-start-1 row-start-1 flex w-full max-w-full flex-col items-center justify-self-center text-center transition-opacity duration-[450ms] ease-in-out',
                    activeSlide === index ? 'intro-slide-active pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
                  )}
                  aria-hidden={activeSlide !== index}
                >
                <div className="flex w-full justify-center px-0 text-center">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="mb-4 block h-[min(92vw,32rem)] w-[min(92vw,32rem)] max-h-[46vh] max-w-none shrink-0 object-contain object-center sm:max-h-[52vh]"
                    draggable={false}
                    loading="eager"
                    decoding="async"
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                  />
                </div>
                <h1 dir="rtl" style={{ textAlign: 'center' }} className="mx-auto w-full max-w-md text-center text-xl font-bold leading-9 text-gray-800 md:text-2xl">
                  {slide.title}
                </h1>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-3" dir="ltr" aria-label="اسلایدهای معرفی">
            {introSlides.map((slide, index) => (
              <button
                key={slide.image}
                type="button"
                aria-label={`رفتن به اسلاید ${index + 1}`}
                aria-current={activeSlide === index}
                onClick={() => setActiveSlide(index)}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  activeSlide === index ? 'w-6 bg-primary-500' : 'w-2 bg-gray-300'
                )}
              />
            ))}
          </div>
        </motion.div>

        {error && (
          <motion.div
            className="w-full mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-center text-sm text-red-800 font-persian"
            variants={pageItem}
          >
            {error}
          </motion.div>
        )}

        <motion.div className="w-full shrink-0 space-y-4 pb-8" variants={pageItem}>
          <Button
            variant="primary"
            size="lg"
            className="w-full bg-primary-500 text-white shadow-lg transition-all duration-300 hover:bg-primary-600 hover:shadow-xl font-bold"
            onClick={handleGetStarted}
            disabled={isLoading}
          >
            {isLoading ? <LoadingSpinner size="sm" /> : 'ثبت نام'}
          </Button>

          <button
            className="w-full py-2 text-sm text-gray-500 transition-colors duration-200 hover:text-primary-500 font-persian"
            onClick={handleAlreadyHaveAccount}
          >
            حساب کاربری دارم
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default WelcomePage;
